import React, { useState } from 'react';
import { Calendar, Clock, Users, ChevronRight } from 'lucide-react';

const Courses = () => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('bootcamp'); // 'bootcamp' or 'mastermind'

  const curriculum = [
    {
      day: 1,
      title: "Kickoff & Orientation",
      description: "Introduction to AI & Machine Learning, bootcamp structure & goals, setting up tools (Google Colab, GitHub, Discord)",
      duration: "3 hours",
      type: "Orientation"
    },
    {
      day: 2,
      title: "Python for AI",
      description: "Python refresher for students, Numpy & Pandas basics, hands-on data wrangling exercises",
      duration: "4 hours",
      type: "Programming"
    },
    {
      day: 3,
      title: "Data Science Foundations",
      description: "Statistics for AI, Exploratory Data Analysis (EDA), hands-on mini dataset analysis",
      duration: "4 hours",
      type: "Data Science"
    },
    {
      day: 4,
      title: "Machine Learning Basics",
      description: "Supervised vs. Unsupervised Learning, Linear Regression & Decision Trees, predict student scores project",
      duration: "5 hours",
      type: "Machine Learning"
    },
    {
      day: 5,
      title: "Deep Learning Intro",
      description: "Neural Networks explained, TensorFlow & PyTorch basics, build your first neural network",
      duration: "5 hours",
      type: "Deep Learning"
    },
    {
      day: 6,
      title: "Computer Vision",
      description: "Image classification concepts, CNN architecture, recognize handwritten digits (MNIST) project",
      duration: "5 hours",
      type: "Computer Vision"
    },
    {
      day: 7,
      title: "NLP Fundamentals",
      description: "Tokenization, embeddings, transformers, hands-on sentiment analysis of tweets",
      duration: "5 hours",
      type: "NLP"
    },
    {
      day: 8,
      title: "Generative AI",
      description: "What is GenAI? Prompt engineering techniques, use GPT models for text generation",
      duration: "4 hours",
      type: "Generative AI"
    },
    {
      day: 9,
      title: "AI Tools for Students",
      description: "Using ChatGPT, GitHub Copilot, Notion AI, AI in academics & projects, AI-assisted coding",
      duration: "3 hours",
      type: "Tools"
    },
    {
      day: 10,
      title: "AI Ethics & Responsible Use",
      description: "Bias in AI, privacy & security, responsible AI practices, student debate session",
      duration: "3 hours",
      type: "Ethics"
    },
    {
      day: 11,
      title: "AI in Real World",
      description: "AI in healthcare, finance, education, case studies & guest speaker session, group discussion",
      duration: "4 hours",
      type: "Industry"
    },
    {
      day: 12,
      title: "Mini Project Ideation",
      description: "Form teams, brainstorm project ideas (AI for good), draft project proposals",
      duration: "3 hours",
      type: "Project"
    },
    {
      day: 13,
      title: "Mini Project Build",
      description: "Coding, dataset collection, model training, mentor support & peer review sessions",
      duration: "6 hours",
      type: "Project"
    },
    {
      day: 14,
      title: "Project Showcase & Graduation",
      description: "Team presentations, feedback from mentors, certificates & discussion of next steps",
      duration: "4 hours",
      type: "Graduation"
    }
  ];

  const mastermindSessions = [
    {
      day: 1,
      session: 1,
      time: "11 AM – 2 PM IST",
      title: "Generative AI Fundamentals",
      description: "Core concepts of Generative AI, overview of capabilities & real-world applications",
      duration: "3 hours",
      type: "Theory + Practice"
    },
    {
      day: 1,
      session: 2,
      time: "3 PM – 4 PM IST",
      title: "Introduction to Diffusion Models",
      description: "Understanding diffusion-based AI models and how they power image/video generation",
      duration: "1 hour",
      type: "Deep Dive"
    },
    {
      day: 1,
      session: 3,
      time: "4 PM – 7 PM IST",
      title: "Image and Video Manipulation",
      description: "Hands-on techniques to create and edit AI visuals, enhancing multimedia with AI tools",
      duration: "3 hours",
      type: "Hands-on"
    },
    {
      day: 2,
      session: 4,
      time: "11 AM – 2 PM IST",
      title: "Building Custom GPTs & Agentic Workflows",
      description: "How to create your own GPTs and building AI workflows for automation",
      duration: "3 hours",
      type: "Advanced"
    },
    {
      day: 2,
      session: 5,
      time: "3 PM – 5 PM IST",
      title: "AI Automations with No-Code",
      description: "Using no-code tools for workflow automation and practical AI use-cases for businesses and creators",
      duration: "2 hours",
      type: "Practical"
    },
    {
      day: 2,
      session: 6,
      time: "After 5 PM IST",
      title: "Workbook-led Activity",
      description: "Guided exercises & implementation to reinforce learning with hands-on practice",
      duration: "2 hours",
      type: "Workshop"
    }
  ];

  const getTypeColor = (type) => {
    const colors = {
      'Orientation': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Programming': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Data Science': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Machine Learning': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Deep Learning': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Computer Vision': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'NLP': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Generative AI': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'Tools': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      'Ethics': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'Industry': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      'Project': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Graduation': 'bg-gold-500/20 text-yellow-400 border-yellow-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getSessionTypeColor = (type) => {
    const colors = {
      'Theory + Practice': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Deep Dive': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Hands-on': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Advanced': 'bg-red-500/20 text-red-400 border-red-500/30',
      'Practical': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Workshop': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  return (
    <section id="courses" className="py-20 bg-black relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white futuristic-text mb-6">
            Our AI Programs
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto mb-8 glow-line"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Choose the perfect AI learning path for your goals - from intensive weekend workshops to comprehensive bootcamps.
          </p>
        </div>

        {/* Course Selection Tabs */}
        <div className="flex justify-center mb-16">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-2 border border-orange-500/20">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <button
                onClick={() => setSelectedCourse('bootcamp')}
                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex flex-col items-center ${
                  selectedCourse === 'bootcamp'
                    ? 'bg-orange-500 text-black shadow-lg shadow-orange-500/20'
                    : 'text-gray-300 hover:text-orange-500'
                }`}
              >
                <span className="text-lg mb-2">🤖 14-Day AI Bootcamp</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm line-through opacity-75">4999 INR</span>
                  <span className="text-lg font-bold">2999 INR</span>
                </div>
                {selectedCourse === 'bootcamp' && (
                  <span className="text-xs mt-1 px-2 py-1 bg-black/20 rounded-full">
                    Save 2000 INR
                  </span>
                )}
              </button>
              <button
                onClick={() => setSelectedCourse('mastermind')}
                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex flex-col items-center ${
                  selectedCourse === 'mastermind'
                    ? 'bg-orange-500 text-black shadow-lg shadow-orange-500/20'
                    : 'text-gray-300 hover:text-orange-500'
                }`}
              >
                <span className="text-lg mb-2">🚀 2-Day AI Mastermind</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm line-through opacity-75">2499 INR</span>
                  <span className="text-lg font-bold">1499 INR</span>
                </div>
                {selectedCourse === 'mastermind' && (
                  <span className="text-xs mt-1 px-2 py-1 bg-black/20 rounded-full">
                    Save 1000 INR
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Course Overview Stats */}
        {selectedCourse === 'bootcamp' ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Calendar, label: "Duration", value: "14 Days" },
              { icon: Clock, label: "Total Hours", value: "58 Hours" },
              { icon: Users, label: "Class Size", value: "Max 25" },
              { icon: ChevronRight, label: "Projects", value: "12+" }
            ].map((stat, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20 text-center glow-card">
                <stat.icon className="w-8 h-8 text-orange-500 mx-auto mb-3" />
                <div className="text-2xl font-bold text-white futuristic-text">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Calendar, label: "Duration", value: "2 Days" },
              { icon: Clock, label: "Total Hours", value: "16 Hours" },
              { icon: Users, label: "Sessions", value: "5 Live" },
              { icon: ChevronRight, label: "Format", value: "Weekend" }
            ].map((stat, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20 text-center glow-card">
                <stat.icon className="w-8 h-8 text-orange-500 mx-auto mb-3" />
                <div className="text-2xl font-bold text-white futuristic-text">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Course Content */}
        {selectedCourse === 'bootcamp' ? (
          <div>
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white futuristic-text mb-4">14-Day Curriculum</h3>
              <p className="text-gray-300 max-w-2xl mx-auto">
                Comprehensive curriculum designed to take you from AI beginner to building real-world projects.
              </p>
            </div>
            
            {/* Curriculum Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {curriculum.map((day, index) => (
                <div 
                  key={index}
                  className={`bg-gray-900/50 backdrop-blur-sm p-6 rounded-xl border transition-all duration-300 cursor-pointer glow-card-hover ${
                    selectedDay === index 
                      ? 'border-orange-500 shadow-lg shadow-orange-500/20' 
                      : 'border-orange-500/20 hover:border-orange-500/40'
                  }`}
                  onClick={() => setSelectedDay(selectedDay === index ? null : index)}
                >
                  {/* Day number and type badge */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <span className="text-orange-500 font-bold futuristic-text">{day.day}</span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(day.type)}`}>
                        {day.type}
                      </span>
                    </div>
                    <span className="text-gray-400 text-sm flex items-center">
                      <Clock size={14} className="mr-1" />
                      {day.duration}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-white mb-3 futuristic-text">
                    Day {day.day}: {day.title}
                  </h3>

                  {/* Description */}
                  <p className={`text-gray-400 text-sm leading-relaxed transition-all duration-300 ${
                    selectedDay === index ? 'max-h-none' : 'line-clamp-2'
                  }`}>
                    {day.description}
                  </p>

                  {/* Expand indicator */}
                  <div className="mt-4 flex justify-center">
                    <ChevronRight 
                      size={16} 
                      className={`text-orange-500 transition-transform duration-300 ${
                        selectedDay === index ? 'rotate-90' : ''
                      }`} 
                    />
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4 h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-1000 ease-out"
                      style={{ width: selectedDay === index ? '100%' : '0%' }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white futuristic-text mb-4">🚀 2-Day AI Mastermind</h3>
              <p className="text-gray-300 max-w-2xl mx-auto mb-6">
                Intensive Weekend Workshop - 16 Hours of AI Training
              </p>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 max-w-md mx-auto">
                <h4 className="text-orange-500 font-semibold mb-2">📅 Schedule</h4>
                <p className="text-gray-300 text-sm">Every Weekend • 10:00 AM IST Start</p>
                <p className="text-gray-300 text-sm">5 Live Expert Sessions • Workbook Activities</p>
              </div>
            </div>

            {/* Mastermind Sessions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {mastermindSessions.map((session, index) => (
                <div 
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm p-6 rounded-xl border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 glow-card-hover"
                >
                  {/* Session header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                        <span className="text-orange-500 font-bold futuristic-text">D{session.day}</span>
                      </div>
                      <div>
                        <span className="text-orange-500 text-sm font-medium">Session {session.session}</span>
                        <div className="text-gray-400 text-xs">{session.time}</div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSessionTypeColor(session.type)}`}>
                      {session.type}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="text-lg font-bold text-white mb-3 futuristic-text">
                    {session.title}
                  </h4>

                  {/* Description */}
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    {session.description}
                  </p>

                  {/* Duration */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs flex items-center">
                      <Clock size={12} className="mr-1" />
                      {session.duration}
                    </span>
                    <div className="h-1 w-16 bg-gradient-to-r from-orange-500 to-orange-400 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>

            {/* Outcomes Section */}
            <div className="bg-gradient-to-r from-orange-500/10 to-transparent p-8 rounded-2xl border border-orange-500/20 max-w-4xl mx-auto">
              <h4 className="text-2xl font-bold text-white mb-6 futuristic-text text-center">🎯 What You'll Achieve</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Build your own AI-driven apps",
                  "Create stunning AI visuals & videos", 
                  "Automate workflows using no-code tools",
                  "Understand diffusion models & generative AI foundations",
                  "Develop and deploy Custom GPTs for your use cases"
                ].map((outcome, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-black text-sm font-bold">✓</span>
                    </div>
                    <span className="text-gray-300">{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-orange-500/10 to-transparent p-8 rounded-2xl border border-orange-500/20 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4 futuristic-text">Ready to Start Your AI Journey?</h3>
            <p className="text-gray-300 mb-6">Join our next cohort and transform your future with cutting-edge AI skills.</p>
            <button className="neon-button-primary px-8 py-3 rounded-lg font-semibold transition-all duration-300">
              Enroll Now - Limited Seats Available
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Courses;